/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, MessageSquare, Paperclip, Loader2, ChevronLeft } from 'lucide-react';
import { api } from '@/services/api';
import { formatDateTimeAR } from '@/utils/dateFormat';
import { compressImage } from '@/utils/imageCompress';
import LoadingIndicator from '@/components/LoadingIndicator';
import { linkify } from '@/utils/linkify';

export default function DirectChatModal({
  thread,
  currentUserId,
  language = 'es',
  onClose,
  onBack,
  onMessagesRead,
  embedded = false,
  renderInParent = false,
  houseName,
  roomNumber,
  viewerIsStaff = false,
  isAdminConversation = false
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [attachedImage, setAttachedImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const load = async () => {
    try {
      const data = await api.listDirectMessages(thread.id);
      setMessages((prev) => {
        if (prev.length !== data.length) return data;
        if (prev[prev.length - 1]?.id !== data[data.length - 1]?.id) return data;
        return prev;
      });
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    }
    setLoading((prev) => (prev ? false : prev));
  };

  useEffect(() => {
    const initial = setTimeout(load, 0);
    const tick = () => { if (document.visibilityState === 'visible') load(); };
    const id = setInterval(tick, 15000);
    return () => { clearTimeout(initial); clearInterval(id); };
  }, [thread.id]);

  useEffect(() => {
    (async () => {
      try {
        await api.markDirectThreadRead(thread.id);
        if (onMessagesRead) onMessagesRead();
      } catch (e) {}
    })();
  }, [thread.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const handlePickImage = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(language === 'es' ? 'Solo se aceptan imágenes.' : 'Images only.');
      return;
    }
    setUploadingImage(true);
    setError('');
    try {
      const compressed = await compressImage(file, { maxWidth: 1200, maxHeight: 1200, quality: 0.72 });
      setAttachedImage(compressed);
    } catch (err) {
      setError(language === 'es' ? 'No se pudo procesar la imagen.' : 'Could not process the image.');
    }
    setUploadingImage(false);
  };

  const send = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!text.trim() && !attachedImage) return;
    setSending(true);
    try {
      const sent = await api.sendDirectMessage(thread.id, text.trim(), attachedImage || undefined);
      setMessages((prev) => [...prev, sent]);
      setText('');
      setAttachedImage(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    }
    setSending(false);
  };

  const headerLabel = !viewerIsStaff && isAdminConversation
    ? (language === 'es' ? 'Administración' : 'Administration')
    : (thread.guest_name || (language === 'es' ? 'Administración' : 'Administration'));

  const chat = (
    <div
      className={embedded
          ? 'direct-chat-shell flex h-full min-h-0 w-full flex-col bg-surface'
          : 'direct-chat-shell bg-surface rounded-2xl w-full max-w-md modal-card flex flex-col'}
      style={embedded ? undefined : { height: 'min(70vh, 560px)', maxHeight: 'calc(100vh - 6rem)' }}
      data-testid={embedded ? 'direct-chat-panel' : undefined}
    >
      <div className="direct-chat-header flex min-h-[72px] items-center justify-between border-b border-border px-5 py-4">
          <div className="flex min-w-0 items-center gap-3.5">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="-ml-2 rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={language === 'es' ? 'Volver a conversaciones' : 'Back to conversations'}
                data-testid="direct-chat-back"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <span className="direct-chat-header__avatar flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
              {!viewerIsStaff && isAdminConversation ? (
                <img
                  src="/logo192.png"
                  alt="WannaColive"
                  className="direct-chat-header__brand-logo brand-logo-adaptive"
                />
              ) : (
                <span className="text-sm font-bold text-muted-foreground shadow-sm">
                  {(thread.guest_name || '?').charAt(0).toUpperCase()}
                </span>
              )}
            </span>
            <div className="min-w-0">
              <h3 className="truncate font-heading text-base font-bold text-foreground">
                {headerLabel}
              </h3>
              {(houseName || roomNumber) && (
                <p className="truncate text-xs text-muted-foreground">
                  {houseName || (language === 'es' ? 'Sin casa' : 'No house')}
                  {roomNumber ? ` · ${language === 'es' ? 'Hab.' : 'Room'} ${roomNumber}` : ''}
                </p>
              )}
            </div>
          </div>
          {!embedded && (
            <button onClick={onClose} className="p-1 text-foreground" aria-label="Close" data-testid="direct-chat-close">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-5 py-5 space-y-3" data-testid="direct-chat-messages">
          {loading ? (
            <LoadingIndicator label={language === 'es' ? 'Cargando…' : 'Loading…'} className="py-6" />
          ) : messages.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
              {language === 'es' ? 'No hay mensajes todavía.' : 'No messages yet.'}
            </div>
          ) : (
            messages.map((m) => {
              // Alineación tipo WhatsApp: huésped a la izquierda, admin/staff a la derecha.
              // Se prioriza el rol del mensaje; si no viene, se cae al matcheo por user_id.
              const senderIsStaff = m.user_role ? m.user_role !== 'guest' : null;
              const mine = senderIsStaff === null
                ? m.user_id === currentUserId
                : (viewerIsStaff ? senderIsStaff : !senderIsStaff);
              return (
                <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`direct-message-bubble max-w-[80%] rounded-2xl px-3 py-2 ${
                    mine
                      ? 'direct-message-mine rounded-br-md'
                      : 'direct-message-received rounded-bl-md'
                  }`}>
                    {!mine && (
                      <p className="text-[10px] font-semibold opacity-70 mb-0.5">
                        {m.user_role === 'guest' ? m.user_name : (language === 'es' ? 'Administración' : 'Admin')}
                      </p>
                    )}
                    {m.image_url && (
                      <button
                        type="button"
                        onClick={() => setLightbox({ url: m.image_url })}
                        className="block border-0 bg-transparent p-0 mb-1 cursor-zoom-in"
                        data-testid={`direct-msg-image-${m.id}`}
                      >
                        <img
                          src={m.image_url}
                          alt=""
                          className="max-w-[200px] max-h-[220px] rounded-lg border border-border"
                        />
                      </button>
                    )}
                    {m.text && <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{linkify(m.text)}</p>}
                    <p className={`direct-message-time mt-1 text-[9px] ${mine ? 'direct-message-time-mine' : 'text-muted-foreground'}`}>
                      {formatDateTimeAR(m.created_at, language === 'es' ? 'es-AR' : 'en-US')}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {attachedImage && (
          <div className="px-4 py-2 border-t border-border flex items-center gap-2" data-testid="direct-chat-attached-preview">
            <img src={attachedImage} alt="" className="w-14 h-14 object-cover rounded-lg border border-border" />
            <span className="text-xs text-muted-foreground flex-1">
              {language === 'es' ? 'Imagen lista para enviar' : 'Image ready to send'}
            </span>
            <button
              type="button"
              onClick={() => setAttachedImage(null)}
              className="text-xs px-2 py-1 rounded border border-border hover:bg-muted"
              data-testid="direct-chat-clear-attach"
            >
              {language === 'es' ? 'Quitar' : 'Remove'}
            </button>
          </div>
        )}

        <form onSubmit={send} className="direct-chat-composer flex items-center gap-2 border-t border-border px-4 py-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            data-testid="direct-chat-file-input"
          />
          <button
            type="button"
            onClick={handlePickImage}
            disabled={uploadingImage || sending}
            className="direct-chat-attach flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-transparent transition-colors disabled:opacity-40"
            aria-label="Adjuntar imagen"
            title={language === 'es' ? 'Adjuntar imagen' : 'Attach image'}
            data-testid="direct-chat-attach"
          >
            {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          </button>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={language === 'es' ? 'Escribí un mensaje…' : 'Type a message…'}
            className="direct-chat-input min-w-0 flex-1 rounded-xl border px-3 py-2.5 text-foreground outline-none transition-all placeholder:text-muted-foreground"
            style={{ fontSize: '16px' }}
            data-testid="direct-chat-input"
            disabled={sending}
            maxLength={2000}
            inputMode="text"
            enterKeyHint="send"
            autoComplete="off"
            autoCorrect="on"
          />
          <button
            type="submit"
            disabled={sending || (!text.trim() && !attachedImage)}
            className="direct-chat-send flex h-10 w-10 shrink-0 items-center justify-center rounded-xl shadow-sm transition-colors focus:outline-none disabled:cursor-not-allowed disabled:shadow-none"
            data-testid="direct-chat-send"
            aria-label="Send"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
        {error && <p className="px-4 pb-3 text-xs text-destructive">{error}</p>}

        {lightbox && createPortal(
          <div
            className="fixed inset-0 bg-black/95 z-[110] flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setLightbox(null)}
            data-testid="direct-chat-lightbox"
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              className="absolute right-4 w-11 h-11 rounded-full bg-white text-black font-bold flex items-center justify-center hover:scale-110 transition-transform shadow-lg z-10"
              style={{ top: 'max(2.5rem, calc(env(safe-area-inset-top, 0px) + 16px))' }}
              aria-label="Cerrar"
              data-testid="direct-chat-lightbox-close"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={lightbox.url}
              alt=""
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body
        )}
      </div>
  );

  if (embedded || renderInParent) return chat;

  return createPortal(
    <div className="fixed inset-0 z-50 modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="direct-chat-modal">
      {chat}
    </div>,
    document.body
  );
}
